import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { TransactionItem } from '../components/shared/TransactionList';
import { CurrencyDisplay } from '../components/shared/CurrencyDisplay';
import { AllowanceScheduler } from '../components/shared/AllowanceScheduler';
import { ChoreManager } from '../components/shared/ChoreRewards';
import InviteManager from '../components/shared/InviteManager';
import { AllocationSlider, AllocationPreview } from '../components/shared/AllocationSlider';
import { 
  Users, LogOut, CheckCircle, XCircle, Clock, 
  DollarSign, Copy, Check, Gift, TrendingUp, Loader2,
  MoreVertical, KeyRound, UserPlus, Building2, Settings, UserCog,
  PieChart, Wallet, PiggyBank, Heart, ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BankerDashboard = () => {
  const { user, logout, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Quick Adjust state
  const [quickAdjustOpen, setQuickAdjustOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [adjustData, setAdjustData] = useState({ amount: '', type: 'bonus', description: '' });
  const [adjusting, setAdjusting] = useState(false);

  // PIN Reset state
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetCustomer, setResetCustomer] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [resetting, setResetting] = useState(false);

  // Add Customer state
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ username: '', pin: '', initial_balance: '0' });
  const [addingCustomer, setAddingCustomer] = useState(false);

  // Bank Settings state
  const [bankSettingsOpen, setBankSettingsOpen] = useState(false);
  const [bankInfo, setBankInfo] = useState({ bank_name: '', tenant_id: '', banker_count: 0, customer_count: 0 });
  const [newBankName, setNewBankName] = useState('');
  const [savingBankName, setSavingBankName] = useState(false);

  // Add Banker state
  const [addBankerOpen, setAddBankerOpen] = useState(false);
  const [newBankerData, setNewBankerData] = useState({ username: '', password: '' });
  const [addingBanker, setAddingBanker] = useState(false);

  // Allocation settings state
  const [allocationOpen, setAllocationOpen] = useState(false);
  const [selectedAllocationCustomer, setSelectedAllocationCustomer] = useState(null);
  const [allocation, setAllocation] = useState({ spend: 70, save: 20, give: 10 });
  const [savingAllocation, setSavingAllocation] = useState(false);

  // Custom allocation for quick adjust
  const [useCustomAllocation, setUseCustomAllocation] = useState(false);
  const [customAllocation, setCustomAllocation] = useState({ spend: 70, save: 20, give: 10 });

  // Transaction approval with allocation
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalTransaction, setApprovalTransaction] = useState(null);
  const [approvalUseCustom, setApprovalUseCustom] = useState(false);
  const [approvalAllocation, setApprovalAllocation] = useState({ spend: 70, save: 20, give: 10 });

  // Balance distribution state
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [distributeCustomer, setDistributeCustomer] = useState(null);
  const [distributeAllocation, setDistributeAllocation] = useState({ spend: 70, save: 20, give: 10 });
  const [distributing, setDistributing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBankInfo();
  }, []);

  const fetchBankInfo = async () => {
    try {
      const res = await axios.get(`${API}/banker/bank-info`);
      setBankInfo(res.data);
      setNewBankName(res.data.bank_name || '');
    } catch (error) {
      console.error('Failed to fetch bank info:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/banker/customers`),
        axios.get(`${API}/banker/pending-transactions`)
      ]);
      setCustomers(customersRes.data);
      setPendingTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransaction = async (transactionId, status, customAlloc = null) => {
    setProcessingId(transactionId);
    try {
      const payload = { status };
      if (customAlloc) {
        payload.custom_allocation = {
          spend_percent: customAlloc.spend,
          save_percent: customAlloc.save,
          give_percent: customAlloc.give
        };
      }
      await axios.patch(`${API}/banker/transactions/${transactionId}`, payload);
      toast.success(`Transaction ${status}`);
      fetchData();
      refreshUser();
      setApprovalDialogOpen(false);
      setApprovalTransaction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${status} transaction`);
    } finally {
      setProcessingId(null);
    }
  };

  // Open approval dialog for deposits to allow custom allocation
  const openApprovalDialog = (transaction) => {
    setApprovalTransaction(transaction);
    setApprovalUseCustom(false);
    setApprovalAllocation({ spend: 70, save: 20, give: 10 });
    setApprovalDialogOpen(true);
  };

  // Handle approval with allocation
  const handleApprovalSubmit = () => {
    if (!approvalTransaction) return;
    const customAlloc = approvalUseCustom ? approvalAllocation : null;
    handleTransaction(approvalTransaction.id, 'approved', customAlloc);
  };

  const handleQuickAdjust = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    
    setAdjusting(true);
    try {
      const payload = {
        amount: parseFloat(adjustData.amount),
        type: adjustData.type,
        description: adjustData.description || null
      };
      
      // Add custom allocation if enabled
      if (useCustomAllocation) {
        payload.custom_allocation = {
          spend_percent: customAllocation.spend,
          save_percent: customAllocation.save,
          give_percent: customAllocation.give
        };
      }
      
      await axios.post(`${API}/banker/quick-adjust/${selectedCustomer.id}`, payload);
      toast.success(`${adjustData.type === 'interest' ? 'Interest' : 'Bonus'} added successfully!`);
      setQuickAdjustOpen(false);
      setAdjustData({ amount: '', type: 'bonus', description: '' });
      setSelectedCustomer(null);
      setUseCustomAllocation(false);
      setCustomAllocation({ spend: 70, save: 20, give: 10 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add adjustment');
    } finally {
      setAdjusting(false);
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    if (!resetCustomer) return;
    
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      toast.error('Please enter a 4-digit PIN');
      return;
    }

    setResetting(true);
    try {
      await axios.post(`${API}/banker/customers/${resetCustomer.id}/reset-password`, {
        new_password: newPin
      });
      toast.success(`PIN reset for ${resetCustomer.username}`);
      setResetPasswordOpen(false);
      setNewPin('');
      setResetCustomer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    } finally {
      setResetting(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (newCustomerData.pin.length !== 4 || !/^\d{4}$/.test(newCustomerData.pin)) {
      toast.error('Please enter a 4-digit PIN');
      return;
    }
    
    setAddingCustomer(true);
    try {
      await axios.post(`${API}/banker/customers`, {
        username: newCustomerData.username,
        password: newCustomerData.pin,
        initial_balance: parseFloat(newCustomerData.initial_balance) || 0
      });
      toast.success(`Customer "${newCustomerData.username}" created successfully!`);
      setAddCustomerOpen(false);
      setNewCustomerData({ username: '', pin: '', initial_balance: '0' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleUpdateBankName = async (e) => {
    e.preventDefault();
    if (!newBankName.trim()) {
      toast.error('Please enter a bank name');
      return;
    }
    
    setSavingBankName(true);
    try {
      await axios.patch(`${API}/banker/bank-info`, { bank_name: newBankName.trim() });
      toast.success('Bank name updated!');
      fetchBankInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bank name');
    } finally {
      setSavingBankName(false);
    }
  };

  const handleAddBanker = async (e) => {
    e.preventDefault();
    if (!newBankerData.username.trim() || !newBankerData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newBankerData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setAddingBanker(true);
    try {
      await axios.post(`${API}/banker/add-banker`, newBankerData);
      toast.success(`Banker "${newBankerData.username}" added to your bank!`);
      setAddBankerOpen(false);
      setNewBankerData({ username: '', password: '' });
      fetchBankInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add banker');
    } finally {
      setAddingBanker(false);
    }
  };

  const openQuickAdjust = (customer, type) => {
    setSelectedCustomer(customer);
    setAdjustData({ amount: '', type, description: '' });
    setUseCustomAllocation(false);
    setCustomAllocation({ spend: 70, save: 20, give: 10 });
    setQuickAdjustOpen(true);
  };

  const openResetPin = (customer) => {
    setResetCustomer(customer);
    setNewPin('');
    setResetPasswordOpen(true);
  };

  // Allocation settings handlers
  const openAllocationSettings = async (customer) => {
    setSelectedAllocationCustomer(customer);
    try {
      const res = await axios.get(`${API}/pots/allocation/${customer.id}`);
      setAllocation({
        spend: res.data.spend_percent,
        save: res.data.save_percent,
        give: res.data.give_percent
      });
    } catch (error) {
      // Use defaults
      setAllocation({ spend: 70, save: 20, give: 10 });
    }
    setAllocationOpen(true);
  };

  const handleSaveAllocation = async () => {
    if (!selectedAllocationCustomer) return;
    
    setSavingAllocation(true);
    try {
      await axios.put(`${API}/pots/allocation/${selectedAllocationCustomer.id}`, {
        spend_percent: allocation.spend,
        save_percent: allocation.save,
        give_percent: allocation.give
      });
      toast.success(`Allocation settings saved for ${selectedAllocationCustomer.username}`);
      setAllocationOpen(false);
      setSelectedAllocationCustomer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save allocation settings');
    } finally {
      setSavingAllocation(false);
    }
  };

  // Balance distribution handlers
  const openDistributeBalance = (customer) => {
    setDistributeCustomer(customer);
    setDistributeAllocation({ spend: 70, save: 20, give: 10 });
    setDistributeOpen(true);
  };

  const handleDistributeBalance = async () => {
    if (!distributeCustomer) return;
    
    setDistributing(true);
    try {
      const res = await axios.post(`${API}/pots/distribute-balance/${distributeCustomer.id}`, {
        spend_percent: distributeAllocation.spend,
        save_percent: distributeAllocation.save,
        give_percent: distributeAllocation.give
      });
      toast.success(res.data.message);
      setDistributeOpen(false);
      setDistributeCustomer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute balance');
    } finally {
      setDistributing(false);
    }
  };

  const copyBankerId = async () => {
    try {
      await navigator.clipboard.writeText(user.id);
      setCopied(true);
      toast.success('Banker ID copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.info(`Your Banker ID: ${user.id}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const totalVaultValue = customers.reduce((sum, c) => sum + c.balance, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-800 rounded-lg flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-slate-800 truncate">Banker Portal</h1>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{user?.username}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setBankSettingsOpen(true)} 
              data-testid="bank-settings-btn"
              className="px-2 sm:px-3"
            >
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Bank Settings</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="px-2 sm:px-3">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Bank Name & Banker ID Card */}
        <Card className="mb-6 bg-gradient-to-r from-slate-800 to-slate-700 text-white" data-testid="banker-id-card">
          <CardContent className="pt-6 px-4 sm:px-6">
            {/* Bank Name */}
            {bankInfo.bank_name && (
              <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-600">
                <Building2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span className="text-lg sm:text-xl font-semibold">{bankInfo.bank_name}</span>
                <Badge variant="secondary" className="bg-slate-600 text-xs">
                  {bankInfo.banker_count} banker{bankInfo.banker_count !== 1 ? 's' : ''} • {bankInfo.customer_count} kid{bankInfo.customer_count !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
            {!bankInfo.bank_name && (
              <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-600">
                <Building2 className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-amber-300">No bank name set</span>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setBankSettingsOpen(true)}
                >
                  Set Bank Name
                </Button>
              </div>
            )}
            
            <div className="flex flex-col gap-4">
              <div className="min-w-0">
                <p className="text-slate-300 text-sm mb-1">Your Banker ID</p>
                <p className="font-mono text-sm sm:text-base lg:text-lg break-all">{user?.id}</p>
                <p className="text-slate-400 text-xs mt-2">Share this with your children to link accounts</p>
              </div>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={copyBankerId}
                data-testid="copy-banker-id"
                className="self-start"
              >
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied!' : 'Copy ID'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <Card data-testid="stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Vault Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Total in Vault</span>
                <CurrencyDisplay amount={totalVaultValue} size="sm" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Linked Customers</span>
                <Badge variant="secondary">{customers.length}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Pending Approvals</span>
                <Badge variant={pendingTransactions.length > 0 ? "destructive" : "secondary"}>
                  {pendingTransactions.length}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Customers List with Actions */}
          <Card className="lg:col-span-2" data-testid="customers-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Linked Customers</CardTitle>
                <CardDescription>Manage your children's accounts</CardDescription>
              </div>
              <Button 
                onClick={() => setAddCustomerOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="add-customer-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            </CardHeader>
            <CardContent>
              {customers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No customers linked yet</p>
                  <p className="text-slate-400 text-sm">Click "Add Customer" to create a new account</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customers.map((customer) => (
                    <div 
                      key={customer.id} 
                      className="flex flex-col p-3 sm:p-4 bg-slate-50 rounded-xl gap-3"
                      data-testid={`customer-${customer.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-sky-600 font-semibold">
                            {customer.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-slate-700 truncate block">{customer.username}</span>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <DollarSign className="w-3 h-3" />
                            <CurrencyDisplay amount={customer.balance} size="sm" className="text-slate-600" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 items-center flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50 border-blue-200 flex-1 sm:flex-none"
                          onClick={() => openQuickAdjust(customer, 'interest')}
                          data-testid={`interest-btn-${customer.id}`}
                        >
                          <TrendingUp className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Interest</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-purple-600 hover:bg-purple-50 border-purple-200 flex-1 sm:flex-none"
                          onClick={() => openQuickAdjust(customer, 'bonus')}
                          data-testid={`bonus-btn-${customer.id}`}
                        >
                          <Gift className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Bonus</span>
                        </Button>
                        
                        {/* More Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              data-testid={`more-actions-${customer.id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => openAllocationSettings(customer)}
                              data-testid={`allocation-settings-${customer.id}`}
                            >
                              <PieChart className="w-4 h-4 mr-2" />
                              Allocation Settings
                            </DropdownMenuItem>
                            {customer.balance > 0 && (
                              <DropdownMenuItem 
                                onClick={() => openDistributeBalance(customer)}
                                data-testid={`distribute-balance-${customer.id}`}
                              >
                                <ArrowRightLeft className="w-4 h-4 mr-2" />
                                Distribute to Pots
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => openResetPin(customer)}
                              data-testid={`reset-pin-${customer.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              Reset PIN
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Allowance Scheduler */}
        <AllowanceScheduler customers={customers} className="mt-6" />

        {/* Chore Rewards */}
        <ChoreManager customers={customers} className="mt-6" />

        {/* Invites & Co-Parents */}
        <div className="mt-6">
          <InviteManager token={token} />
        </div>

        {/* Pending Transactions (Approval Queue) */}
        <Card className="mt-6" data-testid="pending-transactions-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Approval Queue
            </CardTitle>
            <CardDescription>Review and approve customer requests</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingTransactions.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                <p className="text-slate-500">All caught up!</p>
                <p className="text-slate-400 text-sm">No pending transactions</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingTransactions.map((tx) => (
                  <div 
                    key={tx.id} 
                    className="flex flex-col p-3 sm:p-4 border border-slate-200 rounded-xl gap-3"
                    data-testid={`transaction-${tx.id}`}
                  >
                    <TransactionItem 
                      transaction={tx} 
                      showUsername={true}
                      showStatus={false}
                      variant="minimal"
                      className="flex-1 p-0 bg-transparent"
                    />
                    <div className="flex gap-2 self-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 border-red-200"
                        onClick={() => handleTransaction(tx.id, 'rejected')}
                        disabled={processingId === tx.id}
                        data-testid={`reject-${tx.id}`}
                      >
                        <XCircle className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Reject</span>
                      </Button>
                      {tx.type === 'deposit' ? (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openApprovalDialog(tx)}
                          disabled={processingId === tx.id}
                          data-testid={`approve-${tx.id}`}
                        >
                          <CheckCircle className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Approve</span>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleTransaction(tx.id, 'approved')}
                          disabled={processingId === tx.id}
                          data-testid={`approve-${tx.id}`}
                        >
                          <CheckCircle className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Approve</span>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Quick Adjust Dialog */}
      <Dialog open={quickAdjustOpen} onOpenChange={setQuickAdjustOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quick-adjust-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {adjustData.type === 'interest' ? (
                <TrendingUp className="w-5 h-5 text-blue-500" />
              ) : (
                <Gift className="w-5 h-5 text-purple-500" />
              )}
              Add {adjustData.type === 'interest' ? 'Interest' : 'Bonus'}
            </DialogTitle>
            <DialogDescription>
              Add money to {selectedCustomer?.username}'s account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickAdjust} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="adjust-amount">Amount ($)</Label>
              <Input
                id="adjust-amount"
                data-testid="adjust-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={adjustData.amount}
                onChange={(e) => setAdjustData({ ...adjustData, amount: e.target.value })}
                placeholder="0.00"
                required
                className="text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adjust-description">Reason (optional)</Label>
              <Input
                id="adjust-description"
                data-testid="adjust-description"
                value={adjustData.description}
                onChange={(e) => setAdjustData({ ...adjustData, description: e.target.value })}
                placeholder={adjustData.type === 'interest' ? 'Monthly interest' : 'Good behavior bonus'}
              />
            </div>

            {/* Custom Allocation Toggle */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <PieChart className="w-4 h-4 text-slate-500" />
                  Custom Allocation
                </Label>
                <Switch
                  checked={useCustomAllocation}
                  onCheckedChange={setUseCustomAllocation}
                  data-testid="custom-allocation-toggle"
                />
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {useCustomAllocation 
                  ? "Customize how this money is distributed to pots" 
                  : "Uses default allocation (70% Spend, 20% Save, 10% Give)"}
              </p>
              
              {useCustomAllocation && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <AllocationSlider
                    spendPercent={customAllocation.spend}
                    savePercent={customAllocation.save}
                    givePercent={customAllocation.give}
                    onChange={setCustomAllocation}
                    compact
                  />
                  {adjustData.amount && parseFloat(adjustData.amount) > 0 && (
                    <AllocationPreview 
                      amount={parseFloat(adjustData.amount)}
                      spend={customAllocation.spend}
                      save={customAllocation.save}
                      give={customAllocation.give}
                    />
                  )}
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className={`w-full ${adjustData.type === 'interest' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}`}
              disabled={adjusting}
              data-testid="submit-adjust"
            >
              {adjusting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add {adjustData.type === 'interest' ? 'Interest' : 'Bonus'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reset-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-500" />
              Reset PIN
            </DialogTitle>
            <DialogDescription>
              Set a new 4-digit PIN for {resetCustomer?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPin} className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label htmlFor="new-pin" className="text-center block">New PIN</Label>
              <div className="flex gap-3 justify-center">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    id={`reset-pin-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={newPin[index] || ''}
                    onChange={(e) => {
                      const digit = e.target.value;
                      if (!/^\d*$/.test(digit)) return;
                      const pinArray = newPin.split('');
                      pinArray[index] = digit;
                      setNewPin(pinArray.join('').slice(0, 4));
                      if (digit && index < 3) {
                        document.getElementById(`reset-pin-${index + 1}`)?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !newPin[index] && index > 0) {
                        document.getElementById(`reset-pin-${index - 1}`)?.focus();
                      }
                    }}
                    className="w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 border-amber-300 
                             bg-amber-50 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 
                             transition-all outline-none"
                    data-testid={`reset-pin-input-${index}`}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-500 text-center">Enter 4 digits</p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={resetting || newPin.length !== 4}
              data-testid="submit-reset-pin"
            >
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Reset PIN
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerOpen} onOpenChange={setAddCustomerOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-customer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-500" />
              Add New Customer
            </DialogTitle>
            <DialogDescription>
              Create a new customer account linked to your banker account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCustomer} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="customer-username">Username</Label>
              <Input
                id="customer-username"
                data-testid="customer-username-input"
                value={newCustomerData.username}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, username: e.target.value })}
                placeholder="e.g., tommy, sarah"
                required
                minLength={3}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-center block">4-Digit PIN</Label>
              <div className="flex gap-3 justify-center">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    id={`customer-pin-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={newCustomerData.pin[index] || ''}
                    onChange={(e) => {
                      const digit = e.target.value;
                      if (!/^\d*$/.test(digit)) return;
                      const pinArray = newCustomerData.pin.split('');
                      pinArray[index] = digit;
                      setNewCustomerData({ ...newCustomerData, pin: pinArray.join('').slice(0, 4) });
                      if (digit && index < 3) {
                        document.getElementById(`customer-pin-${index + 1}`)?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !newCustomerData.pin[index] && index > 0) {
                        document.getElementById(`customer-pin-${index - 1}`)?.focus();
                      }
                    }}
                    className="w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 border-emerald-300 
                             bg-emerald-50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 
                             transition-all outline-none"
                    data-testid={`customer-pin-input-${index}`}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-500 text-center">This will be their login PIN</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer-balance">Initial Balance ($)</Label>
              <Input
                id="customer-balance"
                data-testid="customer-balance-input"
                type="number"
                step="0.01"
                min="0"
                value={newCustomerData.initial_balance}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, initial_balance: e.target.value })}
                placeholder="0.00"
              />
              <p className="text-xs text-slate-500">Optional starting balance for the account</p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={addingCustomer || newCustomerData.username.length < 3 || newCustomerData.pin.length !== 4}
              data-testid="submit-add-customer"
            >
              {addingCustomer ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Customer
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bank Settings Dialog */}
      <Dialog open={bankSettingsOpen} onOpenChange={setBankSettingsOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="bank-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Bank Settings
            </DialogTitle>
            <DialogDescription>
              Manage your family bank settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Bank Name Section */}
            <form onSubmit={handleUpdateBankName} className="space-y-3">
              <Label htmlFor="bank-name-input">Bank Name</Label>
              <div className="flex gap-2">
                <Input
                  id="bank-name-input"
                  data-testid="bank-name-input"
                  value={newBankName}
                  onChange={(e) => setNewBankName(e.target.value)}
                  placeholder="e.g., The Smith Family Bank"
                  className="flex-1"
                />
                <Button 
                  type="submit" 
                  disabled={savingBankName || !newBankName.trim()}
                  data-testid="save-bank-name"
                >
                  {savingBankName ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </form>

            {/* Divider */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Team Management
              </h4>
              
              {/* Bank Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-slate-800">{bankInfo.banker_count}</p>
                  <p className="text-sm text-slate-500">Bankers</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-slate-800">{bankInfo.customer_count}</p>
                  <p className="text-sm text-slate-500">Kids</p>
                </div>
              </div>

              {/* Add Banker Button */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setBankSettingsOpen(false);
                  setAddBankerOpen(true);
                }}
                disabled={!bankInfo.tenant_id}
                data-testid="add-banker-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Another Banker
              </Button>
              {!bankInfo.tenant_id && (
                <p className="text-xs text-amber-600 mt-2">Set a bank name first to add other bankers</p>
              )}
            </div>

            {/* Banker ID for Sharing */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="text-sm text-slate-600">Your Banker ID (for inviting others)</Label>
              <div className="flex gap-2 mt-2">
                <Input 
                  value={user?.id || ''} 
                  readOnly 
                  className="font-mono text-sm bg-slate-50"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={copyBankerId}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Share this code with other parents to join your bank</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Banker Dialog */}
      <Dialog open={addBankerOpen} onOpenChange={setAddBankerOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-banker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-500" />
              Add New Banker
            </DialogTitle>
            <DialogDescription>
              Add another parent/banker to {bankInfo.bank_name || 'your bank'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddBanker} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new-banker-username">Username</Label>
              <Input
                id="new-banker-username"
                data-testid="new-banker-username"
                value={newBankerData.username}
                onChange={(e) => setNewBankerData({ ...newBankerData, username: e.target.value })}
                placeholder="e.g., mom, dad"
                required
                minLength={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-banker-password">Password</Label>
              <Input
                id="new-banker-password"
                data-testid="new-banker-password"
                type="password"
                value={newBankerData.password}
                onChange={(e) => setNewBankerData({ ...newBankerData, password: e.target.value })}
                placeholder="Min 8 characters"
                required
                minLength={8}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={addingBanker || newBankerData.username.length < 3 || newBankerData.password.length < 8}
              data-testid="submit-add-banker"
            >
              {addingBanker ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add Banker
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Allocation Settings Dialog */}
      <Dialog open={allocationOpen} onOpenChange={setAllocationOpen}>
        <DialogContent className="sm:max-w-md" data-testid="allocation-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-indigo-500" />
              Allocation Settings
            </DialogTitle>
            <DialogDescription>
              Set default income allocation for {selectedAllocationCustomer?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              When {selectedAllocationCustomer?.username} receives money (deposits, bonuses, allowances), 
              it will be automatically split across their pots based on these percentages.
            </p>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <AllocationSlider
                spendPercent={allocation.spend}
                savePercent={allocation.save}
                givePercent={allocation.give}
                onChange={setAllocation}
              />
            </div>
            
            <Button 
              onClick={handleSaveAllocation}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={savingAllocation}
              data-testid="save-allocation-btn"
            >
              {savingAllocation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deposit Approval Dialog with Allocation */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Approve Deposit
            </DialogTitle>
            <DialogDescription>
              Approve ${approvalTransaction?.amount?.toFixed(2)} deposit for {approvalTransaction?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Transaction Details */}
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Amount:</span>
                <span className="text-xl font-bold text-green-600">${approvalTransaction?.amount?.toFixed(2)}</span>
              </div>
              {approvalTransaction?.description && (
                <p className="text-sm text-slate-500 mt-1">{approvalTransaction.description}</p>
              )}
            </div>

            {/* Custom Allocation Toggle */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <PieChart className="w-4 h-4 text-slate-500" />
                  Custom Allocation
                </Label>
                <Switch
                  checked={approvalUseCustom}
                  onCheckedChange={setApprovalUseCustom}
                  data-testid="approval-custom-allocation-toggle"
                />
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {approvalUseCustom 
                  ? "Customize how this deposit is distributed (e.g., birthday gift all to Spend)" 
                  : "Uses default allocation settings"}
              </p>
              
              {approvalUseCustom && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <AllocationSlider
                    spendPercent={approvalAllocation.spend}
                    savePercent={approvalAllocation.save}
                    givePercent={approvalAllocation.give}
                    onChange={setApprovalAllocation}
                    compact
                  />
                  <AllocationPreview 
                    amount={approvalTransaction?.amount || 0}
                    spend={approvalAllocation.spend}
                    save={approvalAllocation.save}
                    give={approvalAllocation.give}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => {
                  handleTransaction(approvalTransaction?.id, 'rejected');
                  setApprovalDialogOpen(false);
                }}
                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                disabled={processingId === approvalTransaction?.id}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
              <Button 
                onClick={handleApprovalSubmit}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={processingId === approvalTransaction?.id}
                data-testid="confirm-approval-btn"
              >
                {processingId === approvalTransaction?.id ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Balance Distribution Dialog */}
      <Dialog open={distributeOpen} onOpenChange={setDistributeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="distribute-balance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-500" />
              Distribute Balance to Pots
            </DialogTitle>
            <DialogDescription>
              Move {distributeCustomer?.username}'s existing balance into their pots
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Balance */}
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-sm text-slate-600 mb-1">Current Balance</p>
              <p className="text-3xl font-bold text-amber-600">${distributeCustomer?.balance?.toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">This will be moved to pots</p>
            </div>

            <p className="text-sm text-slate-600">
              Choose how to distribute this balance across {distributeCustomer?.username}'s pots:
            </p>
            
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <AllocationSlider
                spendPercent={distributeAllocation.spend}
                savePercent={distributeAllocation.save}
                givePercent={distributeAllocation.give}
                onChange={setDistributeAllocation}
              />
              <AllocationPreview 
                amount={distributeCustomer?.balance || 0}
                spend={distributeAllocation.spend}
                save={distributeAllocation.save}
                give={distributeAllocation.give}
              />
            </div>
            
            <Button 
              onClick={handleDistributeBalance}
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={distributing}
              data-testid="confirm-distribute-btn"
            >
              {distributing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
              Distribute to Pots
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankerDashboard;
